import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDeals } from "@/pages/deals/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { buildClosedRecordState } from "@/components/layouts/Activities/components/ActivityClosedRecordEmptyState";
import StageSelection from "@/components/controls/StageSelection";
import { DealStatus } from "@/pages/deals/types";
import DealTopFields from "@/pages/deals/components/DealTopFields";
import DealDetailFields from "@/pages/deals/components/DealDetailFields/DealDetailFields";
import DetailedView, { TabOption } from "@/components/layouts/DetailedView";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { Check, Close } from "@dreamhub/designsystem/icons";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import Ownership from "@/components/layouts/Ownership";
import DealKPIs from "@/pages/deals/components/DealKPIs";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import NextSteps from "@/components/layouts/NextSteps/NextSteps";
import { useDealActions } from "@/services/hooks/entities/deal/useDealActions";
import FromDreamer from "@/components/layouts/FromDreamer/FromDreamer";
import { useIsDemoDeal } from "@/assets/mockupData";
import { ComputedFields, EntityData } from "@/services/api/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import FromDreamerForDemo from "@/components/layouts/FromDreamer/FromDreamerForDemo";
import GuardrailsFailureModal from "@/components/layouts/GuardrailsFailureModal/GuardrailsFailureModal";
import { GuardrailsModalState } from "@/components/layouts/GuardrailsFailureModal/types";
import DealStageWorkflows from "@/pages/deals/components/DealStages/DealStageWorkflows";
import ClosedWonAnimation from "@/pages/deals/components/ClosedWonAnimation";
import { useAuthUserOrNull } from "@frontegg/react";
import {
  getCurrencySymbol,
  monetaryValueFormatter,
  resolveClosedWonContract
} from "@/pages/deals/helpers";
import { useContract, useUpdateDealCurrency } from "@/pages/contracts/hooks";
import { DealClosedWonBanner } from "@/pages/deals/components/DealClosedWonBanner";

const DealDetailedView: React.FC = () => {
  const { t } = useTranslation();

  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const authUser = useAuthUserOrNull();

  // State for closed won animation
  const [showClosedWonAnimation, setShowClosedWonAnimation] = useState(false);

  const [isClosedWonBannerDismissed, setIsClosedWonBannerDismissed] =
    useState(false);

  const {
    entities: [dealEntity],
    isError: isDealError
  } = useDeals([dealId || ""], {
    enabled: !!dealId,
    computed_fields: ComputedFields.ALL,
    forceRefetch: true
  });

  const handleGuardrailsError = (dealId: string, errorData: any) => {
    setGuardrailsModalState({
      isOpen: true,
      dealId,
      deal: dealEntity || null,
      errorData
    });
  };

  // Callback to trigger the closed won animation
  const handleClosedWonSuccess = useCallback(() => {
    setShowClosedWonAnimation(true);
  }, []);

  const {
    deal,
    updateDealState,
    viewParam,
    stages,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField: baseOnUpdateField,
    getTopOptions,
    getOptions,
    searchParams,
    currentStage,
    handleOpenCloseDealModal,
    renderModals,
    isFirstStageDeal,
    canCloseWon
  } = useDealActions(
    dealEntity,
    undefined,
    true,
    undefined,
    handleGuardrailsError,
    true
  );

  const updateDealCurrency = useUpdateDealCurrency(
    deal?.contractId ?? undefined
  );
  const { data: contract, isLoading: isContractLoading } = useContract(
    deal?.contractId ?? undefined
  );

  const onUpdateField: typeof baseOnUpdateField = useCallback(
    (field, newValue, ...rest) => {
      if (
        field === "currency" &&
        deal?.id &&
        deal?.contractId &&
        !contract?.isDetachedFromDeal &&
        typeof newValue === "string"
      ) {
        updateDealCurrency.mutate({
          pathParams: { dealId: deal.id },
          data: { currency: newValue }
        });
      }
      return baseOnUpdateField(field, newValue, ...rest);
    },
    [
      baseOnUpdateField,
      updateDealCurrency,
      deal?.id,
      deal?.contractId,
      contract?.isDetachedFromDeal
    ]
  );

  const openQuestionnaire = searchParams.get("openQuestionnaire");

  const isDemo = useIsDemoDeal(deal?.id || "");

  // Once a deal reaches a terminal state its activity moves to a linked record;
  // the Activities tab shows a closed-record empty state pointing there.
  const dealStatus = deal?.status;
  const dealContractId = deal?.contractId;
  const dealCompanyId = deal?.companyId;
  // After closed-won, deals.contractId points at the frozen snapshot; the
  // record the customer manages onward is the LIVE contract the snapshot was
  // duplicated from — that's where both the banner and "converted to
  // contract" link must navigate, never to the snapshot itself.
  const { isResolved: isClosedWonContractResolved, liveContractId: resolvedLiveContractId } =
    resolveClosedWonContract(dealContractId, contract, isContractLoading);
  const closedWonBannerContractId = isClosedWonContractResolved
    ? resolvedLiveContractId
    : undefined;
  const closedRecordState = useMemo(() => {
    switch (dealStatus) {
      case DealStatus.CLOSED_WON: {
        if (!isClosedWonContractResolved) {
          return undefined;
        }
        return resolvedLiveContractId
          ? buildClosedRecordState({
              sourceEntityType: EntityType.DEAL,
              targetEntityType: EntityType.CONTRACT,
              targetId: resolvedLiveContractId,
              action: t("activities.closedRecord.convertedTo", {
                target: t("entities.names.contract")
              }),
              navigate,
              t
            })
          : undefined;
      }
      case DealStatus.CLOSED_LOST:
        return dealCompanyId
          ? buildClosedRecordState({
              sourceEntityType: EntityType.DEAL,
              targetEntityType: EntityType.COMPANY,
              targetId: dealCompanyId,
              action: t("activities.closedRecord.closedLost"),
              navigate,
              t
            })
          : undefined;
      default:
        return undefined;
    }
  }, [
    dealStatus,
    dealCompanyId,
    isClosedWonContractResolved,
    resolvedLiveContractId,
    navigate,
    t
  ]);

  const [openQuestionnaireId, setOpenQuestionnaireId] = useState<
    string | undefined
  >(openQuestionnaire || undefined);
  const [activeTabOverride, setActiveTabOverride] = useState<
    string | undefined
  >(openQuestionnaire ? TabOption.STAGE_WORKFLOWS.toString() : undefined);

  useEffect(() => {
    if (openQuestionnaire) {
      setActiveTabOverride(TabOption.STAGE_WORKFLOWS.toString());
      setOpenQuestionnaireId(openQuestionnaire);
    }
  }, [openQuestionnaire]);
  const [guardrailsModalState, setGuardrailsModalState] =
    useState<GuardrailsModalState>({
      isOpen: false,
      dealId: null,
      deal: null,
      errorData: null
    });

  const { isDropdownClosing, onDropdownOpenChange, onDropdownSelect } =
    useDropdownClosing();

  const handleOpenQuestionnaire = (questionnaireId?: string) => {
    setActiveTabOverride(TabOption.STAGE_WORKFLOWS.toString());
    setOpenQuestionnaireId(questionnaireId);
  };

  const CloseWonItem = (
    <DropdownMenuItem
      onSelect={() => {
        onDropdownSelect(() => {
          if (!isDropdownClosing) {
            handleOpenCloseDealModal(DealStatus.CLOSED_WON);
          }
        });
      }}
      disabled={isFirstStageDeal || !canCloseWon}
    >
      <div className="flex gap-2 items-center">
        <IconContainer size="5" color="secondary">
          <Check />
        </IconContainer>
        <Text variant="body">{t("deals.dealView.closeWon")}</Text>
      </div>
    </DropdownMenuItem>
  );

  return (
    <div className="relative h-full w-full">
      <DetailedView
        entityData={deal as EntityData}
        entityType={EntityType.DEAL}
        isError={isDealError}
        closedRecordState={closedRecordState}
        pageLink={`/deals?view=${viewParam}`}
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        currentStage={currentStage}
        topRightComponent={
          <>
            <StageSelection
              data-test="deal-detailed-view-stage-selection"
              stages={stages}
              currentStageId={currentStage?.id}
              onStageSelection={(newStageId: number) => {
                const newStage = stages?.find(
                  (stage) => stage.id === newStageId
                );
                return onUpdateField(
                  "stage",
                  newStageId,
                  undefined,
                  t("deals.dealView.movedStage", {
                    dealName: deal?.name,
                    stageName: newStage?.name
                  })
                );
              }}
            />
            <DropdownMenu onOpenChange={onDropdownOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button
                  data-test="deal-detailed-view-close-deal-button"
                  variant="secondary"
                  disabled={
                    deal?.status === DealStatus.CLOSED_WON ||
                    deal?.status === DealStatus.CLOSED_LOST
                  }
                >
                  {t("deals.dealView.closeDeal")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                data-test="deal-detailed-view-close-deal-menu"
                align="end"
                sideOffset={4}
                className="flex flex-col w-fit !bg-surface-elevation1 border border-border-border1 shadow-lg"
              >
                {isFirstStageDeal || !canCloseWon ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full">{CloseWonItem}</div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="left"
                      sideOffset={10}
                      className="w-56"
                    >
                      {canCloseWon
                        ? t("deals.dealView.disabledCloseWon")
                        : t("deals.dealView.closeWonNotPermitted")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  CloseWonItem
                )}
                <DropdownMenuItem
                  onSelect={() => {
                    onDropdownSelect(() => {
                      if (!isDropdownClosing) {
                        handleOpenCloseDealModal(DealStatus.CLOSED_LOST);
                      }
                    });
                  }}
                >
                  <div className="flex gap-2 items-center">
                    <IconContainer size="5" color="secondary">
                      <Close />
                    </IconContainer>
                    <Text variant="body">
                      {t("deals.dealView.closeLost")}
                    </Text>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        topOptions={getTopOptions()}
        options={getOptions()}
        topFields={
          <DealTopFields
            data-test="deal-detailed-view-top-fields"
            deal={deal}
            onUpdateField={onUpdateField}
            handleOpenCloseDealModal={handleOpenCloseDealModal}
          />
        }
        overviewContent={
          <div
            data-test="deal-detailed-view-overview"
            className="flex flex-col w-full gap-6"
          >
            {deal?.status === DealStatus.CLOSED_WON &&
              closedWonBannerContractId &&
              !isClosedWonBannerDismissed && (
                <DealClosedWonBanner
                  contractId={closedWonBannerContractId}
                  onDismiss={() => setIsClosedWonBannerDismissed(true)}
                />
              )}
            <DealKPIs
              data-test="deal-detailed-view-kpis"
              deal={deal}
              onOpenQuestionnaire={handleOpenQuestionnaire}
              onUpdateField={onUpdateField}
            />
            <div className="w-full flex gap-6">
              <div className="flex-1 flex flex-col gap-6">
                {isDemo ? (
                  <FromDreamerForDemo entityType={EntityType.DEAL} />
                ) : (
                  <FromDreamer
                    entityType={EntityType.DEAL}
                    entityId={deal?.id || ""}
                    alerts={deal?.alerts}
                    showSummary={true}
                  />
                )}
                <DealDetailFields deal={deal} onUpdateField={onUpdateField} />
              </div>
              <div className="flex-1 flex flex-col gap-6">
                <NextSteps
                  entity={deal}
                  entityType={EntityType.DEAL}
                  toggleTasksDrawer={toggleTasksDrawer}
                />
                <Ownership
                  entity={deal}
                  entityType={EntityType.DEAL}
                  onUpdateField={onUpdateField}
                  className="h-fit"
                />
              </div>
            </div>
          </div>
        }
        stageWorkflowsContent={
          <DealStageWorkflows
            data-test="deal-detailed-view-stage-workflows"
            deal={deal}
            openQuestionnaireId={openQuestionnaireId}
            onQuestionnaireOpened={() => setOpenQuestionnaireId(undefined)}
          />
        }
        commercialsTopContent={
          deal?.status === DealStatus.CLOSED_WON &&
          closedWonBannerContractId &&
          !isClosedWonBannerDismissed ? (
            <DealClosedWonBanner
              contractId={closedWonBannerContractId}
              title={t("deals.dealView.closedWonBanner.messageTerms")}
              onDismiss={() => setIsClosedWonBannerDismissed(true)}
              size="small"
              buttonVariant="tertiary"
            />
          ) : undefined
        }
        activeTabOverride={activeTabOverride}
        onTabChange={() => {
          if (activeTabOverride) {
            setActiveTabOverride(undefined);
          }
        }}
        onDealUpdate={updateDealState}
      />
      <ClosedWonAnimation
        isVisible={showClosedWonAnimation}
        onAnimationComplete={() => setShowClosedWonAnimation(false)}
        userName={authUser?.name || authUser?.email?.split("@")[0] || ""}
        dealName={deal?.name || ""}
        companyName={deal?.companyName || ""}
        amount={
          deal?.arrLocal
            ? monetaryValueFormatter(
                deal.arrLocal,
                getCurrencySymbol(deal.currency || "USD") || "$"
              )
            : ""
        }
      />
      {renderModals(
        isDropdownClosing,
        false,
        undefined,
        handleClosedWonSuccess
      )}
      <GuardrailsFailureModal
        isOpen={guardrailsModalState.isOpen}
        setIsOpen={(value) => {
          setGuardrailsModalState((prevState) => ({
            ...prevState,
            isOpen:
              typeof value === "function" ? value(prevState.isOpen) : value
          }));
        }}
        dealId={guardrailsModalState.dealId}
        deal={guardrailsModalState.deal}
        errorData={guardrailsModalState.errorData}
      />
    </div>
  );
};

export default DealDetailedView;
